import {
  NewEventTemplateTranslation,
  NewVenueTemplateTranslation,
  NewContentTemplateTranslation,
  Translation,
  EventTemplateTranslation,
  EventTemplateTranslatable,
  VenueTemplateTranslatable,
  ContentTemplateTranslatable,
  ContentTemplateTranslation,
  VenueTemplateTranslation,
  Translatable,
  NewTranslation,
  EventTemplateTransforms,
  VenueTemplateTransforms,
  ContentTemplateTransforms,
  ActivableTranslationTransform,
  ActivableTranslationTransforms,
  AutoResolveConflictStrategy,
  NewSegmentTranslation,
  SegmentTranslation,
  SegmentTransforms,
  LevelTransforms,
  LevelTranslation,
  NewLevelTranslation,
  AttributeTransforms,
  AttributeTranslation,
  NewAttributeTranslation,
  VenueTypeTransforms,
  VenueTypeTranslation,
  NewVenueTypeTranslation,
  MealPeriodTransforms,
  MealPeriodTranslation,
  NewMealPeriodTranslation,
  DayThemeTranslation,
  NewDayThemeTranslation,
  DayThemeTransforms,
  HighlightTranslation,
  HighlightTransforms,
  NewHighlightTranslation,
  CommonTextTransforms,
  NewCommonTextTranslation,
  CommonTextTranslation,
} from '../../shared/types/translation';
import { TranslationModel } from '../../server/models/translations';
import { hasOwnProperty, unsafeDeepCopy } from './utils';
import { mapBy } from './functional';
import { Language } from '../../shared/types/info';
import { UpdateQuery } from 'mongoose';

export function generateDefaultNewTranslation(
  refers: string,
  type: 'eventtemplate'
): NewEventTemplateTranslation;
export function generateDefaultNewTranslation(
  refers: string,
  type: 'venuetemplate'
): NewVenueTemplateTranslation;
export function generateDefaultNewTranslation(
  refers: string,
  type: 'contenttemplate'
): NewContentTemplateTranslation;
export function generateDefaultNewTranslation(
  refers: string,
  type: 'segment'
): NewSegmentTranslation;
export function generateDefaultNewTranslation(refers: string, type: 'level'): NewLevelTranslation;
export function generateDefaultNewTranslation(
  refers: string,
  type: 'attribute'
): NewAttributeTranslation;
export function generateDefaultNewTranslation(
  refers: string,
  type: 'venuetype'
): NewVenueTypeTranslation;
export function generateDefaultNewTranslation(
  refers: string,
  type: 'mealperiod'
): NewMealPeriodTranslation;
export function generateDefaultNewTranslation(
  refers: string,
  type: 'daytheme'
): NewDayThemeTranslation;
export function generateDefaultNewTranslation(
  refers: string,
  type: 'highlight'
): NewHighlightTranslation;
export function generateDefaultNewTranslation(
  refers: string,
  type: 'commonText',
  property: string
): NewCommonTextTranslation;
export function generateDefaultNewTranslation(
  refers: string,
  type: Translation['type']
): Pick<NewTranslation, 'refers' | 'type'> {
  return {
    refers,
    type,
  };
}

export function getTemplateTranslationTransformKey<T extends keyof EventTemplateTranslatable>(
  translation: EventTemplateTranslation | undefined,
  lang: string,
  key: T
): EventTemplateTranslatable[T] | undefined;
export function getTemplateTranslationTransformKey<T extends keyof VenueTemplateTranslatable>(
  translation: VenueTemplateTranslation | undefined,
  lang: string,
  key: T
): VenueTemplateTranslatable[T] | undefined;
export function getTemplateTranslationTransformKey<T extends keyof ContentTemplateTranslatable>(
  translation: ContentTemplateTranslation | undefined,
  lang: string,
  key: T
): ContentTemplateTranslatable[T] | undefined;
export function getTemplateTranslationTransformKey<T extends keyof Translatable>(
  translation: Translation | undefined,
  lang: string,
  key: T
) {
  if (!translation) return undefined;
  const { transforms } = translation;
  if (!transforms || !transforms[lang] || !transforms[lang].active) return undefined;
  return transforms[lang].delta[key];
}

export function makeDefaultEventTemplateTranslationTransforms(
  languages: string[]
): EventTemplateTransforms {
  const result: EventTemplateTransforms = {};
  languages.forEach(lang => {
    result[lang] = {
      active: false,
      timestamp: '',
      delta: {
        title: '',
        description: '',
      },
    };
  });
  return result;
}

export function makeDefaultVenueTemplateTranslationTransforms(
  languages: string[]
): VenueTemplateTransforms {
  const result: VenueTemplateTransforms = {};
  languages.forEach(lang => {
    result[lang] = {
      active: false,
      timestamp: '',
      delta: {
        name: '',
      },
    };
  });
  return result;
}

export function makeDefaultContentTemplateTranslationTransforms(
  languages: string[]
): ContentTemplateTransforms {
  const result: ContentTemplateTransforms = {};

  languages.forEach(lang => {
    result[lang] = {
      active: false,
      timestamp: '',
      delta: {
        content: '',
        name: '',
        description: '',
      },
    };
  });

  return result;
}

export function makeDefaultSegmentTranslationTransforms(languages: string[]): SegmentTransforms {
  const result: SegmentTransforms = {};
  languages.forEach(lang => {
    result[lang] = {
      active: false,
      timestamp: '',
      delta: {
        name: '',
      },
    };
  });
  return result;
}

export function makeDefaultLevelTranslationTransforms(languages: string[]): LevelTransforms {
  const result: LevelTransforms = {};
  languages.forEach(lang => {
    result[lang] = {
      active: false,
      timestamp: '',
      delta: {
        name: '',
      },
    };
  });
  return result;
}

export function makeDefaultAttributeTranslationTransforms(
  languages: string[]
): AttributeTransforms {
  const result: AttributeTransforms = {};
  languages.forEach(lang => {
    result[lang] = {
      active: false,
      timestamp: '',
      delta: {
        name: '',
      },
    };
  });
  return result;
}

export function makeDefaultVenueTypeTranslationTransforms(
  languages: string[]
): VenueTypeTransforms {
  const result: VenueTypeTransforms = {};
  languages.forEach(lang => {
    result[lang] = {
      active: false,
      timestamp: '',
      delta: {
        name: '',
      },
    };
  });
  return result;
}

export function makeDefaultMealPeriodTranslationTransforms(
  languages: string[]
): MealPeriodTransforms {
  const result: MealPeriodTransforms = {};
  languages.forEach(lang => {
    result[lang] = {
      active: false,
      timestamp: '',
      delta: {
        name: '',
      },
    };
  });
  return result;
}

export function makeDefaultDayThemeTranslationTransforms(languages: string[]): DayThemeTransforms {
  const result: DayThemeTransforms = {};
  languages.forEach(lang => {
    result[lang] = {
      active: false,
      timestamp: '',
      delta: {
        name: '',
      },
    };
  });
  return result;
}

export function makeDefaultHighlightTranslationTransforms(
  languages: string[]
): HighlightTransforms {
  const result: HighlightTransforms = {};
  languages.forEach(lang => {
    result[lang] = {
      active: false,
      timestamp: '',
      delta: {
        name: '',
      },
    };
  });
  return result;
}

export function makeDefaultCommonTextTranslationTransforms(
  languages: string[]
): CommonTextTransforms {
  const result: CommonTextTransforms = {};
  languages.forEach(lang => {
    result[lang] = {
      active: false,
      timestamp: '',
      delta: {
        name: '',
      },
    };
  });
  return result;
}

export function isBlankEventTemplateTranslationTransform(
  transform: ActivableTranslationTransform<EventTemplateTranslatable>
) {
  return (
    !transform.active &&
    !transform.timestamp &&
    transform.delta.title === '' &&
    transform.delta.description === ''
  );
}

// GOTCHA: two undefined transforms are not seen as equivalent / timestamps not considered
export function areEquivalentEventTemplateTranslationTransform(
  transf1?: ActivableTranslationTransform<EventTemplateTranslatable>,
  transf2?: ActivableTranslationTransform<EventTemplateTranslatable>
) {
  if (!transf1 || !transf2) return false;

  return (
    transf1.active === transf2.active &&
    transf1.delta.title === transf2.delta.title &&
    transf1.delta.description === transf2.delta.description
  );
}

export function areEquivalentEventTemplateTranslationTransforms(
  transfs1?: ActivableTranslationTransforms<EventTemplateTranslatable>,
  transfs2?: ActivableTranslationTransforms<EventTemplateTranslatable>
) {
  if (!transfs1 || !transfs2) return false;

  const transfs1Keys = Object.keys(transfs1);
  const transfs2Keys = Object.keys(transfs2);

  if (transfs1Keys.length !== transfs2Keys.length) return false;

  const equivalent = transfs1Keys.reduce((acc, x) => {
    acc =
      acc &&
      !!transfs2[x] &&
      areEquivalentEventTemplateTranslationTransform(transfs1[x], transfs2[x]);

    return acc;
  }, true);

  return equivalent;
}

// this creates transforms given translations defaulting if not found
export function makeContentTranslationTransform(
  languages: string[],
  transl?: ContentTemplateTranslation
) {
  const transforms = makeDefaultContentTemplateTranslationTransforms(languages);

  languages.forEach(lang => {
    const oldTransform =
      transl && transl.transforms && hasOwnProperty(transl.transforms, lang)
        ? transl.transforms[lang]
        : undefined;

    if (!oldTransform) return;

    transforms[lang] = oldTransform;
  });

  return transforms;
}

// this creates transforms given translations defaulting if not found
export function makeEventTranslationTransform(
  languages: string[],
  transl?: EventTemplateTranslation | NewEventTemplateTranslation
) {
  const transforms = makeDefaultEventTemplateTranslationTransforms(languages);

  languages.forEach(lang => {
    const oldTransform =
      transl && transl.transforms && hasOwnProperty(transl.transforms, lang)
        ? transl.transforms[lang]
        : undefined;

    if (!oldTransform) return;

    transforms[lang] = oldTransform;
  });

  return transforms;
}

// this creates transforms given translations defaulting if not found
export function makeVenueTranslationTransform(
  languages: string[],
  transl?: VenueTemplateTranslation
) {
  const transforms = makeDefaultVenueTemplateTranslationTransforms(languages);

  languages.forEach(lang => {
    const oldTransform =
      transl && transl.transforms && hasOwnProperty(transl.transforms, lang)
        ? transl.transforms[lang]
        : undefined;

    if (!oldTransform) return;

    transforms[lang] = oldTransform;
  });

  return transforms;
}

export function makeSegmentTranslationTransform(languages: string[], transl?: SegmentTranslation) {
  const transforms = makeDefaultSegmentTranslationTransforms(languages);

  languages.forEach(lang => {
    const oldTransform =
      transl && transl.transforms && hasOwnProperty(transl.transforms, lang)
        ? transl.transforms[lang]
        : undefined;

    if (!oldTransform) return;

    transforms[lang] = oldTransform;
  });

  return transforms;
}

export function makeLevelTranslationTransform(languages: string[], transl?: LevelTranslation) {
  const transforms = makeDefaultLevelTranslationTransforms(languages);

  languages.forEach(lang => {
    const oldTransform =
      transl && transl.transforms && hasOwnProperty(transl.transforms, lang)
        ? transl.transforms[lang]
        : undefined;

    if (!oldTransform) return;

    transforms[lang] = oldTransform;
  });

  return transforms;
}

export function makeAttributeTranslationTransform(
  languages: string[],
  transl?: AttributeTranslation
) {
  const transforms = makeDefaultAttributeTranslationTransforms(languages);

  languages.forEach(lang => {
    const oldTransform =
      transl && transl.transforms && hasOwnProperty(transl.transforms, lang)
        ? transl.transforms[lang]
        : undefined;

    if (!oldTransform) return;

    transforms[lang] = oldTransform;
  });

  return transforms;
}

export function makeVenueTypeTranslationTransform(
  languages: string[],
  transl?: VenueTypeTranslation
) {
  const transforms = makeDefaultVenueTypeTranslationTransforms(languages);

  languages.forEach(lang => {
    const oldTransform =
      transl && transl.transforms && hasOwnProperty(transl.transforms, lang)
        ? transl.transforms[lang]
        : undefined;

    if (!oldTransform) return;

    transforms[lang] = oldTransform;
  });

  return transforms;
}

export function makeMealPeriodTranslationTransform(
  languages: string[],
  transl?: MealPeriodTranslation
) {
  const transforms = makeDefaultMealPeriodTranslationTransforms(languages);

  languages.forEach(lang => {
    const oldTransform =
      transl && transl.transforms && hasOwnProperty(transl.transforms, lang)
        ? transl.transforms[lang]
        : undefined;

    if (!oldTransform) return;

    transforms[lang] = oldTransform;
  });

  return transforms;
}

export function makeDayThemeTranslationTransform(
  languages: string[],
  transl?: DayThemeTranslation
) {
  const transforms = makeDefaultDayThemeTranslationTransforms(languages);

  languages.forEach(lang => {
    const oldTransform =
      transl && transl.transforms && hasOwnProperty(transl.transforms, lang)
        ? transl.transforms[lang]
        : undefined;

    if (!oldTransform) return;

    transforms[lang] = oldTransform;
  });

  return transforms;
}

export function makeHighlightTranslationTransform(
  languages: string[],
  transl?: HighlightTranslation
) {
  const transforms = makeDefaultHighlightTranslationTransforms(languages);

  languages.forEach(lang => {
    const oldTransform =
      transl && transl.transforms && hasOwnProperty(transl.transforms, lang)
        ? transl.transforms[lang]
        : undefined;

    if (!oldTransform) return;

    transforms[lang] = oldTransform;
  });

  return transforms;
}

export function makeCommonTextTranslationTransform(
  languages: string[],
  transl?: CommonTextTranslation
) {
  const transforms = makeDefaultCommonTextTranslationTransforms(languages);

  languages.forEach(lang => {
    const oldTransform =
      transl && transl.transforms && hasOwnProperty(transl.transforms, lang)
        ? transl.transforms[lang]
        : undefined;

    if (!oldTransform) return;

    transforms[lang] = oldTransform;
  });

  return transforms;
}

export interface TranslationFilters {
  selectedLanguages: Language[];
  published?: boolean;
  translated?: boolean;
  createdFrom?: string;
  createdTo?: string;
}

export type TranslationFiltersSimple = Omit<TranslationFilters, 'selectedLanguages'> & {
  selectedLanguages: string[];
};

export type CommonTranslationFilters = Omit<TranslationFilters, 'selectedLanguages'> & {
  page: number;
  perPage: number;
  search?: string;
  propertyId?: string;
};

export type BaseTranslationAggregationParams = {
  // Translations
  translations?: boolean;

  // Translation filters
  translated?: boolean;
  published?: boolean;
};

export interface TranslationRow {
  id: string;
  timestamp: string;
  created_at: string;
  title: string;
  description: string;
  language: string;
  published: boolean;
  translated_title: string;
  translated_description: string;
}

export type TranslationConflictRow = TranslationRow & {
  my_published: boolean;
  their_published: boolean;
  my_title: string;
  their_title: string;
  my_description: string;
  their_description: string;
};

export async function insertTranslationsFromParsedRows(
  languages: string[],
  parsedRows: TranslationRow[],
  strategy?: AutoResolveConflictStrategy
) {
  const eventTemplateIds = parsedRows.map(r => r.id);
  const translations = (await TranslationModel.find({
    type: 'eventtemplate',
    refers: { $in: eventTemplateIds },
  })
    .lean()
    .exec()) as EventTemplateTranslation[];
  const translationsMap = mapBy(translations, 'refers');

  const translToUpsertMap: {
    [key: string]: EventTemplateTranslation | NewEventTemplateTranslation;
  } = {};

  // optimization 1: get rid of blank records
  let filteredRows = parsedRows.filter(
    r =>
      !isBlankEventTemplateTranslationTransform({
        active: r.published,
        timestamp: r.timestamp,
        delta: {
          title: r.translated_title,
          description: r.translated_description,
        },
      })
  );

  const conflicts: TranslationConflictRow[] = [];

  for (let row of filteredRows) {
    // for every row in the csv (we make a deep copy to preserve original row)
    const dbTransl = translationsMap[row.id] ? unsafeDeepCopy(translationsMap[row.id]) : undefined;

    const activeTransl =
      translToUpsertMap[row.id] ||
      dbTransl ||
      generateDefaultNewTranslation(row.id, 'eventtemplate');

    const activeTransfs = makeEventTranslationTransform(languages, activeTransl);

    let fromTransf = activeTransfs[row.language];
    let toTransf = {
      active: row.published,
      timestamp: Date.now().toString(),
      delta: {
        title: row.translated_title,
        description: row.translated_description,
      },
    };

    const myTimestamp = row.timestamp;
    const theirTimestmp = fromTransf.timestamp || '';

    const areEquivTransf = areEquivalentEventTemplateTranslationTransform(fromTransf, toTransf);
    const timestampsNotEqual = myTimestamp !== theirTimestmp;

    let isConflict = !areEquivTransf && timestampsNotEqual;

    // autoresolve conflicts if strategy is passed
    switch (strategy) {
      case 'mine': // easy strategy, just allow update to pass
        isConflict = false;
        break;
    }

    if (isConflict) {
      conflicts.push({
        ...row,
        timestamp: theirTimestmp,
        my_published: row.published,
        my_title: row.translated_title,
        my_description: row.translated_description,
        their_published: fromTransf.active,
        their_title: fromTransf.delta.title,
        their_description: fromTransf.delta.description,
      });
    } else if (!areEquivTransf) {
      activeTransfs[row.language] = toTransf;
      activeTransl.transforms = activeTransfs;
      translToUpsertMap[row.id] = activeTransl;
    }
  }

  // optimization 2: remove updates that replace with the same object
  Object.keys(translToUpsertMap).forEach(k => {
    const activeTransl = translToUpsertMap[k];
    const dbTransl = translationsMap[activeTransl.refers];

    if (
      dbTransl &&
      areEquivalentEventTemplateTranslationTransforms(dbTransl.transforms, activeTransl.transforms)
    ) {
      delete translToUpsertMap[k];
    }
  });

  await Promise.all(
    Object.values(translToUpsertMap).map(t => {
      const payload: UpdateQuery<EventTemplateTranslation | NewEventTemplateTranslation> = { ...t };
      return TranslationModel.findOneAndUpdate({ refers: t.refers }, payload, {
        upsert: true,
        new: true,
      }).exec();
    })
  );

  return conflicts;
}
